import * as Yup from 'yup';

import { TreatmentPlanResourceVersion } from '../../constants/general';

import type {
  AssessmentReviewSchema,
  MaladaptiveSchema,
  AssessmentMethodsSchema,
  AssessmentSummarySchema,
  AssessmentBarriersSchema,
  VisualSchema,
} from './types/validationSchema';

export const assessmentReviewSchema: AssessmentReviewSchema = Yup.object().shape({
  parent_treatment_plan_id: Yup.number().nullable(),
  date_of_assessment: Yup.string().required(),
  //Reassessment date is only required if a client has a treatment plan in the past
  date_of_reassessment: Yup.string().when('parent_treatment_plan_id', {
    is: (value: number | null) => value !== null,
    then: () => Yup.string().required(),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  date_of_clinical_interview: Yup.string().optional(),
  clinical_interview_or_summary: Yup.string().trim().required().min(60),
  is_diagnostic_reports_reviewed: Yup.string().required(),
  is_previous_assessment_reports_reviewed: Yup.boolean().optional(),
  is_previous_intervention_plans_and_treatment_plans_reviewed: Yup.boolean().optional(),
  is_reports_by_other_providers_reviewed: Yup.boolean().optional(),
  is_school_reports_reviewed: Yup.boolean().optional(),
  is_medical_reports_reviewed: Yup.boolean().optional(),
});

export const maladaptiveSchema: MaladaptiveSchema = Yup.object().shape({
  assessment_version: Yup.string().required(),
  label: Yup.string().trim().required().min(5),
  associated_dsm5_symptom: Yup.string().trim().required(),
  maladaptive_concerns: Yup.string().when('assessment_version', {
    is: (value: string) => value === TreatmentPlanResourceVersion.V2,
    then: () => Yup.string().trim().required().min(60),
    otherwise: () => Yup.string().trim().nullable(),
  }),
});

export const assessmentMethodsSchema: AssessmentMethodsSchema = Yup.object().shape({
  assessment_version: Yup.string().required(),
  tool_name: Yup.string().trim().required(),
  date_tool_used: Yup.string().when('assessment_version', {
    is: (value: string) => value === TreatmentPlanResourceVersion.V2,
    then: () => Yup.string().required(),
    otherwise: () => Yup.string().nullable(),
  }),
  reason_for_use_age_group: Yup.string().trim().required().min(60),
  reason_for_use_symptoms: Yup.string().trim().required().min(60),
  score: Yup.string().trim().required().min(60),
  description: Yup.string().trim().required().min(60),
});

export const assessmentSummarySchema: AssessmentSummarySchema = Yup.object().shape({
  summary_assessment_result: Yup.string().trim().required().min(60),
  summary_priorities_based_on_assessment: Yup.string().trim().required().min(60),
  summary_observations_during_assessment: Yup.string().trim().required().min(60),
});

export const assessmentBarriersSchema: AssessmentBarriersSchema = Yup.object().shape({
  label: Yup.string().trim().required(),
  description: Yup.string()
    .when('label', {
      is: 'Other',
      then: () => Yup.string().trim().required().min(2),
    })
    .when('label', (label, schema) => {
      if (String(label) !== 'Other') {
        return schema.nullable();
      }
      return schema;
    }),
});

export const visualSchema: VisualSchema = Yup.object().shape({
  label: Yup.string().trim().required().min(5),
  description: Yup.string().trim().required().min(60),
});
